/**
 * Quiz Diagnóstico Postural Express para Oficinistas — detecta los
 * padecimientos más frecuentes en personas con trabajo sedentario y
 * mucho tiempo frente al escritorio o la pantalla.
 * URL: /quiz/postural (no visible en menú)
 */

export const QUIZPOST_META = {
  id: "postural-oficinistas",
  title: "Diagnóstico Postural Express para oficinistas",
  intro:
    "Pasar 8+ horas sentado/a frente a una pantalla está silenciosamente moldeando tu cuerpo. " +
    "10 preguntas honestas para detectar los 10 padecimientos más comunes en oficinistas.",
  therapist: {
    name: "Fisioterapeuta Omar Aguilera",
    role: "Fisioterapeuta y Quiropráctico",
    service: "terapia_fisica",
    phoneWa: "527712141634",
    photo: "/img/team/therapist_1_quiro_fisio.png",
  },
};

export const QUIZPOST_QUESTIONS = [
  { id: "q1", text: "Pasas más de 6 horas al día sentado/a frente a una computadora o escritorio.", flags: ["lumbalgia_postural", "contractura_dorsal", "cefalea_tensional"] },
  { id: "q2", text: "Al final del día sientes la espalda baja cargada, tensa o con dolor que mejora al estirarte o acostarte.", flags: ["lumbalgia_postural"] },
  { id: "q3", text: "Tienes dolor o rigidez en el cuello y trapecios, especialmente al final de la jornada o tras videollamadas largas.", flags: ["cervicalgia_text_neck", "contractura_dorsal"] },
  { id: "q4", text: "Sufres dolores de cabeza tensionales recurrentes (frente, sienes o nuca) sobre todo entre semana.", flags: ["cefalea_tensional", "cervicalgia_text_neck"] },
  { id: "q5", text: "Sientes hormigueo, adormecimiento o dolor en la muñeca o dedos al usar el mouse/teclado por mucho tiempo.", flags: ["tunel_carpiano_oficina", "mouse_arm"] },
  { id: "q6", text: "Tu hombro derecho (o el del lado del mouse) duele, truena o pierde rango al levantar el brazo.", flags: ["hombro_oficinista", "mouse_arm"] },
  { id: "q7", text: "Notas que tu postura ha cambiado: hombros caídos hacia adelante, cuello adelantado y/o espalda alta encorvada.", flags: ["sindrome_cruzado_superior", "contractura_dorsal"] },
  { id: "q8", text: "Tienes dolor profundo en un glúteo o que se irradia por detrás del muslo cuando llevas mucho rato sentado/a.", flags: ["piramidal_glúteo", "ciatica_postural"] },
  { id: "q9", text: "Tus rodillas duelen o crujen al levantarte tras estar sentado/a por largos periodos, o al subir y bajar escaleras.", flags: ["rodilla_oficinista"] },
  { id: "q10", text: "Te sientes con poca energía, fatiga visual y respiración corta cuando llevas horas trabajando frente a la pantalla.", flags: ["fatiga_postural", "sindrome_cruzado_superior"] },
];

export const QUIZPOST_CONDITIONS = {
  lumbalgia_postural: {
    name: "Lumbalgia postural crónica",
    short: "Lumbalgia",
    description:
      "Dolor de espalda baja causado por horas de estar sentado/a con mala postura, debilidad del core y acortamiento de flexores de cadera. La causa #1 de incapacidad laboral en oficinistas.",
    treatment:
      "Movilización lumbar + liberación de psoas + activación de core y glúteo medio + reeducación de la sedestación. Mejora notable en 4-6 sesiones.",
    severity: "high",
  },
  cervicalgia_text_neck: {
    name: "Cervicalgia / Síndrome 'text neck'",
    short: "Cuello tensionado",
    description:
      "Sobrecarga cervical por mantener la cabeza adelantada mirando pantalla o celular. Por cada 2.5 cm que la cabeza se adelanta, el cuello soporta 4.5 kg extra.",
    treatment:
      "Masaje cervical profundo + movilización articular + ejercicios de flexores profundos + ergonomía del puesto. Resultados visibles en 3-5 sesiones.",
    severity: "mid",
  },
  contractura_dorsal: {
    name: "Contractura del trapecio y dorsal",
    short: "Contracturas dorsales",
    description:
      "Nudos musculares persistentes entre las escápulas y en el trapecio superior. Causan dolor que parece estar 'siempre ahí' y empeora al final del día.",
    treatment:
      "Punción seca + masaje terapéutico profundo + ejercicios de retracción escapular + pausas activas. Liberación inmediata con plan de mantenimiento.",
    severity: "mid",
  },
  cefalea_tensional: {
    name: "Cefalea tensional",
    short: "Dolor de cabeza tensional",
    description:
      "Dolor de cabeza opresivo originado por tensión sostenida en cuello y trapecios. Suele aparecer entre semana, empeora con el estrés y mejora los fines de semana.",
    treatment:
      "Tratamiento cervical específico + liberación suboccipital + manejo del estrés + corrección postural. Reduce frecuencia 70-80%.",
    severity: "mid",
  },
  tunel_carpiano_oficina: {
    name: "Síndrome del túnel carpiano (forma laboral)",
    short: "Túnel carpiano",
    description:
      "Compresión del nervio mediano en la muñeca por uso intensivo del teclado/mouse con muñeca en extensión. Provoca hormigueo nocturno y pérdida de fuerza al agarrar.",
    treatment:
      "Movilización del nervio mediano + ferulización nocturna + ejercicios de deslizamiento + corrección ergonómica. Resuelve hasta 70% sin cirugía.",
    severity: "high",
  },
  mouse_arm: {
    name: "Síndrome del brazo del ratón ('mouse arm')",
    short: "Mouse arm",
    description:
      "Dolor difuso en antebrazo, codo y hombro del lado dominante por uso prolongado del mouse. Combina tendinitis, sobrecarga y mala postura del hombro.",
    treatment:
      "Ondas de choque + liberación miofascial + ejercicios excéntricos + cambio a mouse vertical o ergonómico. Excelente respuesta en 6 sesiones.",
    severity: "mid",
  },
  hombro_oficinista: {
    name: "Hombro doloroso del oficinista",
    short: "Hombro doloroso",
    description:
      "Tendinitis del manguito rotador o pinzamiento subacromial por hombros redondeados, escápulas elevadas y debilidad del manguito. Duele al elevar el brazo o al dormir del lado afectado.",
    treatment:
      "Ultrasonido terapéutico + ejercicios escapulares + fortalecimiento del manguito rotador + ergonomía. 80% mejora con terapia conservadora.",
    severity: "mid",
  },
  sindrome_cruzado_superior: {
    name: "Síndrome cruzado superior",
    short: "Postura cifótica",
    description:
      "Patrón postural típico del oficinista: hombros adelantados, cabeza protruida y espalda alta encorvada. Origina la mayoría de los dolores cervicales y dorsales crónicos.",
    treatment:
      "Reeducación postural global + fortalecimiento de músculos posteriores (romboides, dorsal) + estiramiento del pectoral. Cambio visible en 8-10 semanas.",
    severity: "high",
  },
  piramidal_glúteo: {
    name: "Síndrome del piramidal",
    short: "Piramidal",
    description:
      "Contractura del músculo piramidal en el glúteo profundo que comprime el nervio ciático. Empeora al estar mucho tiempo sentado/a o cruzar la pierna.",
    treatment:
      "Liberación del piramidal + estiramiento neural + fortalecimiento de glúteo medio + pausas activas. Resultados rápidos en 4-6 sesiones.",
    severity: "mid",
  },
  ciatica_postural: {
    name: "Ciática postural",
    short: "Ciática postural",
    description:
      "Irritación del nervio ciático por compresión postural sostenida (no necesariamente hernia). El sentarse muchas horas comprime el nervio y la postura adelantada agrava el cuadro.",
    treatment:
      "Manipulación quiropráctica suave + estiramiento neural + corrección postural + fortalecimiento del core. Atender pronto evita cronificación.",
    severity: "high",
  },
  rodilla_oficinista: {
    name: "Síndrome de la rodilla del oficinista (condromalacia rotuliana)",
    short: "Rodilla anterior",
    description:
      "Reblandecimiento del cartílago detrás de la rótula por horas de flexión y debilidad del cuádriceps. Duele al pararse tras estar sentado/a o subir escaleras.",
    treatment:
      "Fortalecimiento del vasto medial + reeducación de la pisada + vendaje neuromuscular + pausas activas. Reduce dolor 70% en 6 semanas.",
    severity: "mid",
  },
  fatiga_postural: {
    name: "Fatiga postural y respiratoria",
    short: "Fatiga postural",
    description:
      "Cansancio crónico durante el día por mala mecánica respiratoria (respirar 'corto' por la postura adelantada). Reduce energía, concentración y oxigenación.",
    treatment:
      "Reeducación diafragmática + ejercicios torácicos + apertura del pectoral + higiene postural. Mejora notable en 4-6 sesiones.",
    severity: "mid",
  },
};

export function scoreConditionsPost(answers) {
  const counts = {};
  QUIZPOST_QUESTIONS.forEach((q, i) => {
    if (!answers[i]) return;
    q.flags.forEach((f) => { counts[f] = (counts[f] || 0) + 1; });
  });
  return Object.entries(counts)
    .map(([key, score]) => ({ key, score, ...QUIZPOST_CONDITIONS[key] }))
    .sort((a, b) => b.score - a.score);
}

export function overallSeverityPost(ranked) {
  if (ranked.some((c) => c.severity === "high")) return "red";
  if (ranked.length >= 2) return "yellow";
  if (ranked.length >= 1) return "yellow";
  return "green";
}
